import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Plus,
  ChevronDown,
  Loader,
  Sparkles,
  X,
} from 'lucide-react';
import { templatesAPI, templateFoldersAPI, getErrorMessage } from '@/services/api';
import {
  TemplateCategory,
  TemplatePosition,
  CreateTemplateData,
  TemplateVariable,
} from '@/types/templates';

type EditorTab = 'basic' | 'advanced' | 'preview';

const TemplateEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { templateId } = useParams<{ templateId?: string }>();

  // Determine if we're in edit or create mode
  const isEditing = !!templateId;

  // Form state
  const [tab, setTab] = useState<EditorTab>('basic');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [firstComment, setFirstComment] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('body');
  const [position, setPosition] = useState<TemplatePosition>('end');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [color, setColor] = useState('#cccccc');
  const [platformVariants, setPlatformVariants] = useState<Record<string, string>>({});
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(new Set());
  const [showAISuggest, setShowAISuggest] = useState(false);
  const [aiIntent, setAiIntent] = useState('');
  const [, setIsAutoCategorizingMode] = useState(false);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');
  const [newVarDefault, setNewVarDefault] = useState('');

  // Fetch template if editing
  const { data: template } = useQuery({
    queryKey: ['template', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      const response = await templatesAPI.get(templateId);
      return response.data;
    },
    enabled: isEditing,
  });

  // Fetch folders
  const { data: foldersData } = useQuery({
    queryKey: ['template-folders'],
    queryFn: async () => {
      const response = await templateFoldersAPI.list();
      return response.data;
    },
  });

  // Initialize form with template data
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setContent(template.content);
      setFirstComment(template.first_comment || '');
      setCategory(template.category);
      setPosition(template.position);
      setHashtags(template.hashtags || []);
      setTags(template.tags || []);
      setSelectedFolder(template.folder_id || '');
      setColor(template.color || '#cccccc');
      setPlatformVariants(template.platform_variants || {});
      setVariables(template.variables || []);
    }
  }, [template]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: CreateTemplateData) => {
      if (isEditing && templateId) {
        return templatesAPI.update(templateId, data);
      } else {
        return templatesAPI.create(data);
      }
    },
    onSuccess: () => {
      toast.success(
        isEditing
          ? t('templates_updated', 'Template updated successfully')
          : t('templates_created', 'Template created successfully')
      );
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      navigate('/templates');
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(
          error,
          t('templates_save_error', 'Failed to save template')
        )
      );
    },
  });

  // Auto-categorize mutation
  const categorizeMutation = useMutation({
    mutationFn: () => templatesAPI.aiCategorize({ content }),
    onSuccess: (response: { data: { category: TemplateCategory; position: TemplatePosition } }) => {
      setCategory(response.data.category);
      setPosition(response.data.position);
      toast.success(t('templates_autocategorize_success', 'Category assigned'));
      setIsAutoCategorizingMode(false);
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(
          error,
          t('templates_categorize_error', 'Failed to categorize')
        )
      );
    },
  });

  // AI suggest mutation
  const suggestMutation = useMutation({
    mutationFn: () =>
      templatesAPI.aiSuggest({
        intent: aiIntent,
        category,
      }),
    onSuccess: (response: { data: { suggestions?: Array<{ content: string }> } }) => {
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setContent(response.data.suggestions[0].content);
        toast.success(t('templates_ai_suggest_success', 'Content generated'));
      }
      setShowAISuggest(false);
      setAiIntent('');
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(
          error,
          t('templates_ai_suggest_error', 'Failed to generate content')
        )
      );
    },
  });

  // Event handlers
  const handleAddHashtag = () => {
    if (hashtagInput.trim() && !hashtags.includes(hashtagInput.trim())) {
      setHashtags([...hashtags, hashtagInput.trim()]);
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (tag: string) => {
    setHashtags(hashtags.filter((t) => t !== tag));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleTogglePlatform = (platform: string) => {
    setExpandedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) {
        next.delete(platform);
      } else {
        next.add(platform);
      }
      return next;
    });
  };

  const handleUpdatePlatformVariant = (platform: string, value: string) => {
    setPlatformVariants((prev) => ({
      ...prev,
      [platform]: value,
    }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('templates_error_name_required', 'Template name is required'));
      return;
    }

    if (!content.trim()) {
      toast.error(t('templates_error_content_required', 'Content is required'));
      return;
    }

    const data: CreateTemplateData = {
      name: name.trim(),
      description: description.trim(),
      content: content.trim(),
      category,
      position,
      hashtags,
      tags,
      folder_id: selectedFolder || undefined,
      color,
      platform_variants: platformVariants,
      variables,
    };

    if (firstComment.trim()) {
      data.first_comment = firstComment.trim();
    }

    await saveMutation.mutateAsync(data);
  };

  const folders: Array<{ id: string; name: string }> = foldersData?.folders || [];
  const characterCount = content.length;
  const platforms = ['Twitter', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok'];

  const categoryOptions: Array<{ value: TemplateCategory; label: string }> = [
    { value: 'hook', label: t('templates_category_hooks', 'Hooks') },
    { value: 'body', label: t('templates_category_body', 'Body') },
    { value: 'cta', label: t('templates_category_cta', 'CTA') },
    { value: 'hashtags', label: t('templates_category_hashtags', 'Hashtags') },
    { value: 'first_comment', label: t('templates_category_first_comment', 'First Comment') },
    { value: 'full', label: t('templates_category_full', 'Full') },
  ];

  const positionOptions: Array<{ value: TemplatePosition; label: string }> = [
    { value: 'start', label: t('templates_position_start', 'Start') },
    { value: 'end', label: t('templates_position_end', 'End') },
    { value: 'replace', label: t('templates_position_replace', 'Replace') },
    { value: 'first_comment', label: t('templates_position_first_comment', 'First Comment') },
  ];

  return (
    <div className="h-full flex flex-col bg-n-card">
      {/* Page Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-n-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-n-text">
              {isEditing
                ? t('templates_edit_title', 'Edit Template')
                : t('templates_create_title', 'New Template')}
            </h1>
            <p className="text-sm text-n-text-md mt-1">
              {isEditing
                ? t('templates_edit_description', 'Update your template details')
                : t('templates_create_description', 'Create a new reusable template')}
            </p>
          </div>
          <button
            onClick={() => navigate('/templates')}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded border border-n-border text-n-text-md hover:bg-n-hover"
          >
            <ArrowLeft size={14} />
            {t('templates_action_back', 'Back')}
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-6 py-2 border-b border-n-border">
        <button
          onClick={() => setTab('basic')}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
            tab === 'basic'
              ? 'bg-n-blue text-white'
              : 'text-n-text-md hover:bg-n-hover'
          }`}
        >
          {t('templates_tab_basic', 'Basic')}
        </button>
        <button
          onClick={() => setTab('advanced')}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
            tab === 'advanced'
              ? 'bg-n-blue text-white'
              : 'text-n-text-md hover:bg-n-hover'
          }`}
        >
          {t('templates_tab_advanced', 'Advanced')}
        </button>
        <button
          onClick={() => setTab('preview')}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
            tab === 'preview'
              ? 'bg-n-blue text-white'
              : 'text-n-text-md hover:bg-n-hover'
          }`}
        >
          {t('templates_tab_preview', 'Preview')}
        </button>
      </div>

      {/* Form Content - scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {tab === 'basic' && (
          <div className="space-y-4 max-w-2xl">
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_field_name', 'Template Name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('templates_placeholder_name', 'e.g., "Monday Motivation"')}
                className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_field_description', 'Description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('templates_placeholder_description', 'Describe when to use this template')}
                rows={3}
                className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-n-text">
                  {t('templates_field_content', 'Content')} *
                </label>
                <span className="text-xs text-n-text-md">
                  {characterCount} {t('templates_characters', 'characters')}
                </span>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('templates_placeholder_content', 'Enter your template content')}
                rows={6}
                className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs font-mono"
              />
              <div className="mt-2 flex gap-2 flex-wrap">
                <button
                  onClick={() => setIsAutoCategorizingMode(true)}
                  disabled={categorizeMutation.isPending || !content.trim()}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-60 transition-colors"
                >
                  {categorizeMutation.isPending ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  {t('templates_action_autocategorize', 'Auto-categorize')}
                </button>
                <button
                  onClick={() => setShowAISuggest(!showAISuggest)}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                >
                  <Sparkles size={16} />
                  {t('templates_action_ai_suggest', 'Generate with AI')}
                </button>
              </div>
            </div>

            {/* AI Suggest Prompt */}
            {showAISuggest && (
              <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <label className="block text-xs font-semibold text-n-text">
                  {t('templates_field_ai_intent', 'What should the content be about?')}
                </label>
                <input
                  type="text"
                  value={aiIntent}
                  onChange={(e) => setAiIntent(e.target.value)}
                  placeholder={t('templates_placeholder_ai_intent', 'e.g., "Inspirational quote about growth"')}
                  className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      suggestMutation.mutate();
                    }
                  }}
                />
                <button
                  onClick={() => suggestMutation.mutate()}
                  disabled={suggestMutation.isPending || !aiIntent.trim()}
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-white rounded-lg hover:opacity-90 disabled:opacity-60 transition-colors font-medium text-xs"
                  style={{ backgroundColor: '#2383e2' }}
                >
                  {suggestMutation.isPending && (
                    <Loader size={16} className="animate-spin" />
                  )}
                  {t('templates_button_generate', 'Generate')}
                </button>
              </div>
            )}

            {/* Category and Position */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-n-text mb-1">
                  {t('templates_field_category', 'Category')}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                  className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                >
                  {categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-n-text mb-1">
                  {t('templates_field_position', 'Position')}
                </label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value as TemplatePosition)}
                  className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                >
                  {positionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {tab === 'advanced' && (
          <div className="space-y-4 max-w-2xl">
            {/* First Comment */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_field_first_comment', 'First Comment')}
              </label>
              <textarea
                value={firstComment}
                onChange={(e) => setFirstComment(e.target.value)}
                placeholder={t('templates_placeholder_first_comment', 'Automatically posted as the first comment')}
                rows={3}
                className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
              />
            </div>

            {/* Hashtags */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_field_hashtags', 'Hashtags')}
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  placeholder={t('templates_placeholder_hashtag', '#add_hashtag')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddHashtag();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                />
                <button
                  onClick={handleAddHashtag}
                  className="px-3 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-xs"
                  style={{ backgroundColor: '#2383e2' }}
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center gap-2 px-2 py-1 bg-n-blue/20 text-n-blue rounded-full text-xs"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveHashtag(tag)}
                      className="hover:opacity-80"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_field_tags', 'Tags')}
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder={t('templates_placeholder_tag', 'add_tag')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTag();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                />
                <button
                  onClick={handleAddTag}
                  className="px-3 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-xs"
                  style={{ backgroundColor: '#2383e2' }}
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center gap-2 px-2 py-1 bg-n-hover text-n-text rounded-full text-xs"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:opacity-80"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Color and Folder */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-n-text mb-1">
                  {t('templates_field_color', 'Color')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-10 h-10 border border-n-border rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="flex-1 px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-n-text mb-1">
                  {t('templates_field_folder', 'Folder')}
                </label>
                <select
                  value={selectedFolder}
                  onChange={(e) => setSelectedFolder(e.target.value)}
                  className="w-full px-3 py-2 border border-n-border rounded-lg bg-n-page focus:outline-none focus:border-n-blue text-xs"
                >
                  <option value="">{t('templates_no_folder', 'No folder')}</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Platform Variants */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_field_platform_variants', 'Platform Variants')}
              </label>
              <div className="space-y-2">
                {platforms.map((platform) => (
                  <div key={platform} className="border border-n-border rounded-lg">
                    <button
                      onClick={() => handleTogglePlatform(platform)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-n-hover transition-colors text-xs"
                    >
                      <span className="font-medium text-n-text">{platform}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${
                          expandedPlatforms.has(platform) ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedPlatforms.has(platform) && (
                      <div className="px-3 py-2 bg-n-page border-t border-n-border">
                        <textarea
                          value={platformVariants[platform] || ''}
                          onChange={(e) =>
                            handleUpdatePlatformVariant(
                              platform,
                              e.target.value
                            )
                          }
                          placeholder={`${t('templates_platform_variant_hint', 'Platform-specific content for')} ${platform}`}
                          rows={3}
                          className="w-full px-2 py-2 border border-n-border rounded text-xs bg-n-page focus:outline-none focus:border-n-blue"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Variables */}
            <div>
              <label className="block text-xs font-semibold text-n-text mb-1">
                {t('templates_variables_title', 'Variables')}
              </label>
              <p className="text-[10px] text-n-text-lt mb-2">
                Use {'{{variable}}'} in content to create dynamic placeholders
              </p>

              {variables.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {variables.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 bg-n-page rounded-lg px-2.5 py-1.5">
                      <code className="text-[10px] text-n-blue font-mono">{`{{${v.key}}}`}</code>
                      <span className="text-xs text-n-text flex-1">{v.label}</span>
                      {v.default && (
                        <span className="text-[10px] text-n-text-lt">= {v.default}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setVariables(variables.filter((_, idx) => idx !== i))}
                        className="p-0.5 text-n-text-lt hover:text-n-red"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={newVarKey}
                  onChange={(e) => setNewVarKey(e.target.value.replace(/[^a-z0-9_]/g, ''))}
                  placeholder="key"
                  className="w-20 px-2 py-1.5 border border-n-border rounded-md bg-n-page text-xs focus:outline-none focus:border-n-blue font-mono"
                />
                <input
                  type="text"
                  value={newVarLabel}
                  onChange={(e) => setNewVarLabel(e.target.value)}
                  placeholder="Label"
                  className="flex-1 px-2 py-1.5 border border-n-border rounded-md bg-n-page text-xs focus:outline-none focus:border-n-blue"
                />
                <input
                  type="text"
                  value={newVarDefault}
                  onChange={(e) => setNewVarDefault(e.target.value)}
                  placeholder="Default"
                  className="w-20 px-2 py-1.5 border border-n-border rounded-md bg-n-page text-xs focus:outline-none focus:border-n-blue"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newVarKey && newVarLabel) {
                      setVariables([...variables, { key: newVarKey, label: newVarLabel, default: newVarDefault || undefined }]);
                      setNewVarKey('');
                      setNewVarLabel('');
                      setNewVarDefault('');
                    }
                  }}
                  disabled={!newVarKey || !newVarLabel}
                  className="p-1.5 rounded-md text-white disabled:opacity-40"
                  style={{ backgroundColor: '#2383e2' }}
                >
                  <Plus size={12} />
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'preview' && (
          <div className="space-y-4 max-w-2xl">
            <div className="p-3 bg-n-page rounded-lg border border-n-border">
              <h3 className="font-semibold text-n-text mb-2 text-xs">
                {t('templates_preview_main_content', 'Main Content')}
              </h3>
              <p className="text-n-text whitespace-pre-wrap break-words text-xs">
                {content || t('templates_no_content', '(No content)')}
              </p>
            </div>

            {firstComment && (
              <div className="p-3 bg-n-page rounded-lg border border-n-border">
                <h3 className="font-semibold text-n-text mb-2 text-xs">
                  {t('templates_preview_first_comment', 'First Comment')}
                </h3>
                <p className="text-n-text whitespace-pre-wrap break-words text-xs">
                  {firstComment}
                </p>
              </div>
            )}

            {hashtags.length > 0 && (
              <div className="p-3 bg-n-page rounded-lg border border-n-border">
                <h3 className="font-semibold text-n-text mb-2 text-xs">
                  {t('templates_preview_hashtags', 'Hashtags')}
                </h3>
                <p className="text-n-text text-xs">{hashtags.join(' ')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with Save/Cancel */}
      <div className="flex-shrink-0 px-6 py-3 border-t border-n-border flex items-center justify-end gap-2">
        <button
          onClick={() => navigate('/templates')}
          className="px-4 py-1.5 rounded-lg bg-n-hover text-n-text-md text-xs font-semibold hover:bg-n-page transition-colors"
        >
          {t('templates_button_cancel', 'Cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending || !name.trim() || !content.trim()}
          className="px-4 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 transition-opacity"
          style={{ backgroundColor: '#2383e2' }}
        >
          {saveMutation.isPending && <Loader size={16} className="animate-spin" />}
          {isEditing
            ? t('templates_button_update', 'Update')
            : t('templates_button_create', 'Create')}
        </button>
      </div>
    </div>
  );
};

export default TemplateEditorPage;
